import { Col, Row, Tabs } from 'antd'
import type { ReactNode } from 'react'
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useSearchParams } from 'react-router'

import { useEncounterStaleLock } from './hooks/useEncounterStaleLock'
import { usePrefetchPreviousEncounter } from './hooks/usePrefetchPreviousEncounter'

import Show from 'common/components/Show2'
import { APP_ENVIRONMENT_CONSTANTS } from 'common/constants/appEnvironmentConstants'
import { useAppSelector } from 'common/hooks/redux'
import { env } from 'common/utils/env'
import { BuildInfoBadge } from './components/build-info-badge/BuildInfoBadge'
import { ESignButton } from './components/e-sign-button/ESignButton'
import { getTabSignedStatus } from './components/e-sign-button/e-sign-status.utils'
import { ModelSceneToggleButton } from './components/model-scene-toggle-button'
import { PatientEncounterSelect } from './components/patient-encounter-select'
import { SuggestiveMessage } from './components/suggestive-message/suggestive-message'
import { getTabsConfig, tabsKeys } from './config/tabs.config'
import { LabsPreview } from './features/labs/preview'
import { PreviousVisits } from './features/previous-visits'
import type { FormsNavigationRequest } from './features/signed-documents/signed-documents.types'
import { usePatientEncounterSelect } from './hooks/usePatientEncounterSelect'
import { useSceneActions } from './hooks/useSceneActions'
import type { CanvasCaptureRef } from './procedure-model/canvas-capture'
import { ModelScene, type ModelSceneMode } from './procedure-model/scene'
import styles from './procedure.module.scss'
import { useFetchESignFormQuery, useFetchIntakeTemplatesQuery } from './state/procedure.api'

export const ModelDebugMode = env.ENV === APP_ENVIRONMENT_CONSTANTS.DEVELOPMENT

const LEFT_SECTION_TABS = [tabsKeys.treatments, tabsKeys.photos, tabsKeys.soap, tabsKeys.labs]
const PREVIEW_TABS = [tabsKeys.labs]
const DEFAULT_FEATURE_TAB = 'treatments'
const FEATURE_TAB_PARAM = 'tab'
type TabLeaveAction = (() => Promise<void>) | null

const Procedure = () => {
  const { siteId } = useParams<{ siteId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeMainTab, setActiveMainTab] = useState('selected-visit')
  const [isPreviousTabMounted, setIsPreviousTabMounted] = useState(false)
  const [isPreviousCanvasReady, setIsPreviousCanvasReady] = useState(false)
  const [activeFeatureTab, setActiveFeatureTab] = useState<string>(
    () => searchParams.get(FEATURE_TAB_PARAM) ?? DEFAULT_FEATURE_TAB,
  )

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (activeFeatureTab === DEFAULT_FEATURE_TAB) {
          next.delete(FEATURE_TAB_PARAM)
        } else {
          next.set(FEATURE_TAB_PARAM, activeFeatureTab)
        }
        return next
      },
      { replace: true },
    )
  }, [activeFeatureTab, setSearchParams])
  const [tabActions, setTabActions] = useState<Record<string, ReactNode | null>>({})
  const [tabBarSlot, setTabBarSlot] = useState<HTMLDivElement | null>(null)
  const [formsNavigationRequest, setFormsNavigationRequest] =
    useState<FormsNavigationRequest | null>(null)
  const { handleResetSceneSensitiveData, handleSetShowElementIds, handleSetDrawMode } =
    useSceneActions()
  const canvasCaptureRefHolder = useRef<React.RefObject<CanvasCaptureRef> | null>(null)
  const tabLeaveActionsRef = useRef<Record<string, TabLeaveAction>>({})
  const activeFeatureTabRef = useRef(activeFeatureTab)
  const visitContextChangeQueueRef = useRef<Promise<void>>(Promise.resolve())

  useEffect(() => {
    activeFeatureTabRef.current = activeFeatureTab
  }, [activeFeatureTab])

  const flushActiveFeatureTab = useCallback(async () => {
    const leaveAction = tabLeaveActionsRef.current[activeFeatureTabRef.current]
    if (leaveAction) {
      await leaveAction()
    }
  }, [])

  const executeVisitContextChange = useCallback(
    async (applyChange: () => void | Promise<void>) => {
      const runChange = async () => {
        await flushActiveFeatureTab()
        await applyChange()
      }

      const queuedChange = visitContextChangeQueueRef.current.then(runChange, runChange)
      visitContextChangeQueueRef.current = queuedChange.catch((error) => {
        console.error('Error while applying charts context change:', error)
      })

      return queuedChange
    },
    [flushActiveFeatureTab],
  )

  const patientEncounterSelect = usePatientEncounterSelect({
    executeVisitContextChange,
  })

  const isEncounterStale = useEncounterStaleLock(
    patientEncounterSelect.selectedEncounterUid,
    patientEncounterSelect.encounters,
  )

  usePrefetchPreviousEncounter({
    selectedEncounterUid: patientEncounterSelect.selectedEncounterUid,
    encounters: patientEncounterSelect.encounters,
    siteId,
    selectedPatientId: patientEncounterSelect.selectedPatientId,
  })

  const isModelSceneExpanded = useAppSelector(
    (state) => state.procedureReducer.isModelSceneExpanded,
  )
  const isProcedureDeletePending = useAppSelector(
    (state) => state.procedureReducer.isProcedureDeletePending,
  )
  const selectedComparisonEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedComparisonEncounterUid,
  )

  const isESignArgsReady = Boolean(
    patientEncounterSelect.selectedPatientId && patientEncounterSelect.selectedEncounterUid,
  )
  const {
    data: eSignStatus,
    isLoading: isESignStatusLoading,
    isUninitialized: isESignStatusUninitialized,
    isFetching: isESignStatusFetching,
    isError: isESignStatusError,
  } = useFetchESignFormQuery(
    {
      patientId: patientEncounterSelect.selectedPatientId!,
      encounterUid: patientEncounterSelect.selectedEncounterUid!,
    },
    {
      skip: !isESignArgsReady,
    },
  )
  const [isESignInitialLoadingVisible, setIsESignInitialLoadingVisible] = useState(true)

  useEffect(() => {
    if (!isESignInitialLoadingVisible) {
      return
    }

    if (patientEncounterSelect.isLoading) {
      return
    }

    if (!isESignArgsReady) {
      setIsESignInitialLoadingVisible(false)
      return
    }

    if (
      isESignStatusLoading ||
      isESignStatusUninitialized ||
      (isESignStatusFetching && !eSignStatus)
    ) {
      return
    }

    if (isESignStatusError || eSignStatus || !isESignStatusFetching) {
      setIsESignInitialLoadingVisible(false)
    }
  }, [
    eSignStatus,
    isESignArgsReady,
    isESignInitialLoadingVisible,
    isESignStatusError,
    isESignStatusFetching,
    isESignStatusLoading,
    isESignStatusUninitialized,
    patientEncounterSelect.isLoading,
  ])

  const isESignStatusPending = isESignInitialLoadingVisible
  const {
    data: intakeTemplates = [],
    isLoading: isFormsCountInitialLoading,
    isFetching: isFormsCountFetching,
  } = useFetchIntakeTemplatesQuery(
    { patientId: patientEncounterSelect.selectedPatientId! },
    { skip: !patientEncounterSelect.selectedPatientId },
  )
  const pendingFormsCount = intakeTemplates.length
  const isFormsCountLoading =
    Boolean(patientEncounterSelect.selectedPatientId) &&
    (isFormsCountInitialLoading || isFormsCountFetching)

  const isEncounterSigned = eSignStatus?.encounter?.is_signed === 1
  const isTreatmentsSigned = getTabSignedStatus(eSignStatus, tabsKeys.treatments)
  const isSceneLocked = Boolean(
    isEncounterSigned || isTreatmentsSigned || isProcedureDeletePending || isEncounterStale,
  )

  const isNotesTab = activeFeatureTab === tabsKeys.soap

  const modelSceneMode: ModelSceneMode = isNotesTab ? 'notes' : 'default'
  const inactiveProcedureOpacity = activeFeatureTab === tabsKeys.treatments ? 0.2 : 0

  const handleCanvasCaptureRef = useCallback((ref: React.RefObject<CanvasCaptureRef>) => {
    canvasCaptureRefHolder.current = ref
  }, [])

  const showElementIdsBeforeNotesRef = useRef<boolean | null>(null)
  const showElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
  const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)
  const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
  const drawModeBeforeNotesRef = useRef<typeof drawMode | undefined>(undefined)
  const selectedProcedureRef = useRef(selectedProcedure)

  useEffect(() => {
    selectedProcedureRef.current = selectedProcedure
  }, [selectedProcedure])

  useEffect(() => {
    if (isNotesTab) {
      showElementIdsBeforeNotesRef.current = showElementIds
      drawModeBeforeNotesRef.current = drawMode
      handleSetShowElementIds(true)
      handleSetDrawMode(null)
    }

    return () => {
      if (isNotesTab && showElementIdsBeforeNotesRef.current !== null) {
        handleSetShowElementIds(showElementIdsBeforeNotesRef.current)
        if (drawModeBeforeNotesRef.current !== undefined && selectedProcedureRef.current) {
          handleSetDrawMode(drawModeBeforeNotesRef.current)
        }
        drawModeBeforeNotesRef.current = undefined
        showElementIdsBeforeNotesRef.current = null
      }
    }
  }, [isNotesTab, handleSetShowElementIds, handleSetDrawMode])

  const registerTabAction = useCallback((tabKey: string, action: ReactNode | null) => {
    setTabActions((prev) => ({
      ...prev,
      [tabKey]: action,
    }))
  }, [])

  const registerTabLeaveAction = useCallback((tabKey: string, action: TabLeaveAction) => {
    if (!action) {
      delete tabLeaveActionsRef.current[tabKey]
      return
    }

    tabLeaveActionsRef.current[tabKey] = action
  }, [])

  const handleNavigateToForms = useCallback(
    (documentId?: number | null) => {
      void executeVisitContextChange(() => {
        setFormsNavigationRequest((prev) => ({
          requestId: (prev?.requestId ?? 0) + 1,
          targetSubTab: 'signed',
          documentId: documentId ?? null,
        }))
        setActiveFeatureTab(tabsKeys.forms)
      })
    },
    [executeVisitContextChange],
  )

  const handleFeatureTabChange = useCallback(
    (nextTab: string) => {
      if (isProcedureDeletePending) {
        return
      }

      if (nextTab === activeFeatureTabRef.current) {
        return
      }

      void executeVisitContextChange(() => {
        setActiveFeatureTab(nextTab)
      })
    },
    [executeVisitContextChange, isProcedureDeletePending],
  )

  const featureTabPointerRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    nextKey: string
    moved: boolean
  } | null>(null)

  const handleFeatureTabPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'touch') {
      return
    }

    const target = event.target as HTMLElement | null
    const tabEl = target?.closest('[data-node-key]') as HTMLElement | null
    const nextKey = tabEl?.getAttribute('data-node-key')

    if (!nextKey || nextKey === activeFeatureTabRef.current) {
      featureTabPointerRef.current = null
      return
    }

    featureTabPointerRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      nextKey,
      moved: false,
    }
  }, [])

  const handleFeatureTabPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const tracked = featureTabPointerRef.current
    if (!tracked || tracked.pointerId !== event.pointerId || tracked.moved) {
      return
    }

    const dx = Math.abs(event.clientX - tracked.startX)
    const dy = Math.abs(event.clientY - tracked.startY)

    if (dx > 8 || dy > 8) {
      tracked.moved = true
    }
  }, [])

  const handleFeatureTabPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const tracked = featureTabPointerRef.current
      featureTabPointerRef.current = null

      if (!tracked || tracked.pointerId !== event.pointerId || tracked.moved) {
        return
      }

      if (tracked.nextKey === activeFeatureTabRef.current) {
        return
      }

      handleFeatureTabChange(tracked.nextKey)
    },
    [handleFeatureTabChange],
  )

  const handleFeatureTabPointerCancel = useCallback(() => {
    featureTabPointerRef.current = null
  }, [])

  const isPreviousTabActive = activeMainTab === 'previous-visits'
  const isSelectedTabLoading = !isPreviousTabActive && patientEncounterSelect.isLoading
  const isPreviousTabLoading = isPreviousTabActive && !isPreviousCanvasReady

  const handlePreviousCanvasReady = useCallback(() => {
    setIsPreviousCanvasReady(true)
  }, [])

  useEffect(() => {
    setIsPreviousCanvasReady(false)
  }, [patientEncounterSelect.selectedEncounterUid, selectedComparisonEncounterUid])

  const handleMainTabChange = useCallback(
    (nextTab: string) => {
      if (isProcedureDeletePending) {
        return
      }

      if (activeMainTab === 'selected-visit' && nextTab !== 'selected-visit') {
        void executeVisitContextChange(() => {
          setActiveMainTab(nextTab)
          startTransition(() => {
            setIsPreviousTabMounted(true)
          })
        })
        return
      }

      setActiveMainTab(nextTab)
    },
    [activeMainTab, executeVisitContextChange, isProcedureDeletePending],
  )

  const featureTabs = useMemo(
    () =>
      getTabsConfig(
        registerTabAction,
        registerTabLeaveAction,
        patientEncounterSelect.selectedPatientId,
        patientEncounterSelect.selectedEncounterUid,
        eSignStatus,
        isESignStatusPending,
        isProcedureDeletePending || isEncounterStale,
        handleNavigateToForms,
        pendingFormsCount,
        formsNavigationRequest,
      ),
    [
      registerTabAction,
      registerTabLeaveAction,
      patientEncounterSelect.selectedPatientId,
      patientEncounterSelect.selectedEncounterUid,
      eSignStatus,
      isESignStatusPending,
      isProcedureDeletePending,
      isEncounterStale,
      handleNavigateToForms,
      isFormsCountLoading,
      pendingFormsCount,
      formsNavigationRequest,
    ],
  )

  const hasLeftSection = useMemo(() => {
    return LEFT_SECTION_TABS.includes(activeFeatureTab as (typeof LEFT_SECTION_TABS)[number])
  }, [activeFeatureTab])

  const isPreviewTab = useMemo(() => {
    return PREVIEW_TABS.includes(activeFeatureTab as (typeof PREVIEW_TABS)[number])
  }, [activeFeatureTab])

  const showLeftExpanded = isPreviewTab || isModelSceneExpanded

  const currentVisitContent = useMemo(() => {
    if (!patientEncounterSelect.isLoading && !patientEncounterSelect.selectedPatientId) {
      return (
        <div className={styles.currentVisitContent}>
          <SuggestiveMessage
            title='No Patient Selected'
            subtitle='Please select a patient from the list above or use the search button to add a new patient to view procedure details.'
            icon='mdi:account-search-outline'
          />
        </div>
      )
    }

    if (
      !patientEncounterSelect.isLoading &&
      patientEncounterSelect.selectedPatientId &&
      patientEncounterSelect.encounterOptions.length === 0
    ) {
      return (
        <div className={styles.currentVisitContent}>
          <SuggestiveMessage
            title='No Encounters Found'
            subtitle='The selected patient does not have any recorded encounters. Please use the search to add a new patient or select another one.'
            icon='mdi:clipboard-text-off-outline'
          />
        </div>
      )
    }

    return (
      <div className={styles.currentVisitContent}>
        <div
          ref={setTabBarSlot}
          className={styles.featureTabsBarSlot}
          onPointerDownCapture={handleFeatureTabPointerDown}
          onPointerMoveCapture={handleFeatureTabPointerMove}
          onPointerUpCapture={handleFeatureTabPointerUp}
          onPointerCancelCapture={handleFeatureTabPointerCancel}
        />
        <Row className={styles.currentVisitRow} gutter={[24, 24]}>
          {hasLeftSection && (
            <Col
              className={styles.leftColumn}
              flex={showLeftExpanded ? undefined : '0 0 auto'}
              xs={showLeftExpanded ? 11 : undefined}>
              <Show>
                <Show.When isVisible={activeFeatureTab === tabsKeys.labs}>
                  <LabsPreview
                    selectedPatientId={patientEncounterSelect.selectedPatientId}
                    selectedEncounterUid={patientEncounterSelect.selectedEncounterUid}
                  />
                </Show.When>
                <Show.Else>
                  <Show>
                    <Show.When isVisible={isModelSceneExpanded}>
                      <div className={styles.modelSceneWrapper}>
                        <ModelScene
                          debugMode={ModelDebugMode}
                          selectedPatientId={patientEncounterSelect.selectedPatientId}
                          selectedPatientSex={patientEncounterSelect.patientInfo?.sex ?? null}
                          mode={modelSceneMode}
                          isReadOnly={isSceneLocked}
                          onCanvasCaptureRef={handleCanvasCaptureRef}
                          inactiveProcedureOpacity={inactiveProcedureOpacity}
                        />
                      </div>
                    </Show.When>
                    <Show.Else>
                      <ModelSceneToggleButton />
                    </Show.Else>
                  </Show>
                </Show.Else>
              </Show>
            </Col>
          )}
          <Col
            flex={hasLeftSection && !showLeftExpanded ? '1' : undefined}
            xs={hasLeftSection && showLeftExpanded ? 13 : 24}
            className={styles.rightColumn}>
            <Tabs
              className={styles.featureTabs}
              items={featureTabs}
              // destroyOnHidden
              activeKey={activeFeatureTab}
              onChange={handleFeatureTabChange}
              renderTabBar={(props, DefaultTabBar) =>
                tabBarSlot ? createPortal(<DefaultTabBar {...props} />, tabBarSlot) : null
              }
            />
          </Col>
        </Row>
      </div>
    )
  }, [
    activeFeatureTab,
    featureTabs,
    handleFeatureTabChange,
    handleFeatureTabPointerDown,
    handleFeatureTabPointerMove,
    handleFeatureTabPointerUp,
    handleFeatureTabPointerCancel,
    patientEncounterSelect.selectedPatientId,
    patientEncounterSelect.isLoading,
    patientEncounterSelect.encounterOptions.length,
    patientEncounterSelect.selectedEncounterUid,
    patientEncounterSelect.patientInfo?.sex,
    hasLeftSection,
    showLeftExpanded,
    isModelSceneExpanded,
    modelSceneMode,
    inactiveProcedureOpacity,
    isSceneLocked,
    handleCanvasCaptureRef,
    tabBarSlot,
  ])

  const mainTabs = useMemo(
    () => [
      {
        key: 'selected-visit',
        label: 'Selected',
        disabled: isPreviousTabLoading,
        children: currentVisitContent,
      },
      {
        key: 'previous-visits',
        label: 'Previous',
        disabled: patientEncounterSelect.encounterOptions.length < 2 || isSelectedTabLoading,
        children: isPreviousTabMounted ? (
          <PreviousVisits
            encounterOptions={patientEncounterSelect.encounterOptions}
            selectedPatientSex={patientEncounterSelect.patientInfo?.sex ?? null}
            onCanvasReady={handlePreviousCanvasReady}
          />
        ) : null,
      },
    ],
    [
      currentVisitContent,
      handlePreviousCanvasReady,
      isPreviousTabLoading,
      isPreviousTabMounted,
      isSelectedTabLoading,
      patientEncounterSelect.encounterOptions,
      patientEncounterSelect.patientInfo?.sex,
    ],
  )

  const mainTabsExtraContent =
    activeMainTab === 'selected-visit' ? (
      <div className={styles.controlsLayer}>
        <div className={styles.selectorsContainer}>
          <PatientEncounterSelect
            {...patientEncounterSelect}
            isESignStatusPending={isESignStatusPending}
          />
        </div>
        <div className={styles.actionsContainer}>{tabActions[activeFeatureTab]}</div>
      </div>
    ) : null

  useEffect(() => {
    return () => {
      handleResetSceneSensitiveData()
    }
  }, [handleResetSceneSensitiveData])

  return (
    <div className={styles.container}>
      <BuildInfoBadge />
      <div className={styles.tabsHeaderContainer}>
        <div className={styles.tabsWrapper}>
          <Tabs
            activeKey={activeMainTab}
            onChange={handleMainTabChange}
            className={styles.mainTabs}
            items={mainTabs}
            animated={{ inkBar: true, tabPane: false }}
            renderTabBar={(props, DefaultTabBar) => (
              <div className={styles.mainTabsBar}>
                <DefaultTabBar {...props} />
                {mainTabsExtraContent}
              </div>
            )}
          />
        </div>
      </div>
      {activeMainTab === 'selected-visit' && !isEncounterStale && (
        <ESignButton
          activeTab={activeFeatureTab}
          patientId={patientEncounterSelect.selectedPatientId}
          encounterUid={patientEncounterSelect.selectedEncounterUid}
        />
      )}
    </div>
  )
}

export default Procedure
